import { useRef, useState } from "react"
import { Input, Button, Space } from "antd"
import type { InputRef } from "antd"
import { SearchOutlined } from "@ant-design/icons"
import { ITableColumns } from "../types"

export function useColumnSearch<T>() {
    const [searchText, setSearchText] = useState("")
    const [searchedColumn, setSearchedColumn] = useState("")
    const searchInput = useRef<InputRef>(null)

    const handleSearch = (selectedKeys: React.Key[], confirm: () => void, dataIndex: keyof T) => {
        confirm()
        setSearchText(selectedKeys[0] as string)
        setSearchedColumn(dataIndex as string)
    }

    const handleReset = (setSelectedKeys: (keys: React.Key[]) => void, confirm: () => void) => {
        // Clear the input AND re-apply filters with empty value
        setSelectedKeys([])
        setSearchText("")
        confirm() // triggers table to remove the filter
    }

    const getColumnSearchProps = (dataIndex: keyof T): Omit<ITableColumns<T>, "key"> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: "0.5rem" }}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${String(dataIndex)}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    style={{ marginBottom: 8, display: "block" }}
                />
                <Space>
                    <Button onClick={() => handleReset(setSelectedKeys, confirm)} size="small" style={{ width: "90px" }}>
                        Reset
                    </Button>
                    <Button
                        type="primary"
                        iconPosition="end"
                        onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: "90px" }}
                    >
                        Search
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ padding: "0 0.2rem", color: filtered ? "#1677ff" : undefined }} />
        ),
        onFilter: (value, record) => {
            const cellValue = record[dataIndex]

            if (Array.isArray(cellValue)) {
                return cellValue.some((obj) =>
                    Object.values(obj)
                        .filter((v) => typeof v === "string") // take all string values
                        .join(" ")
                        .toLowerCase()
                        .includes((value as string).toLowerCase())
                )
            } else if (typeof cellValue === "string") {
                return cellValue.toLowerCase().includes((value as string).toLowerCase())
            }
            return false
        }
    })

    return { getColumnSearchProps, searchText, searchedColumn }
}
